import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import isEqual from 'lodash/isEqual';
import { Button, Grid, Pagination, Icon, Table } from 'semantic-ui-react';
import { Formatters } from '../../utils';
import {
  AssetDownloadsTable,
  SearchResultsPlaceholder,
  SortIcon,
  TotalPill,
  Loader
} from '../';
import manualDefault from './manual-default.png';
import manualNoResults from './manual-no-results.png';
import { PAGE_SIZE_OPTIONS } from '../../constants';
import { helpers, style } from '../AssetDownloadsTable';

const { swapImageEndpointForDownload } = helpers;
const { StyledCTACell } = style;
const PAGE_SIZE_OPTIONS_MOBILE = 10;
let sorted = null;

// used for unit test
const sortNames = [
  'ProductBrands',
  'ProductName',
  'FileName',
  'Language',
  'LastModified'
];

class ManualsTable extends PureComponent {
  state = {
    limit: PAGE_SIZE_OPTIONS[0],
    activePage: 1,
    activeItems: null,
    activeSort: null,
    isDesc: {}
  };

  static propTypes = {
    manuals: PropTypes.array,
    isLoading: PropTypes.bool,
    querySelections: PropTypes.object,
    isMobile: PropTypes.bool
  };

  componentDidUpdate(prevProps) {
    const { activePage, limit } = this.state;
    const { manuals, isMobile } = this.props;
    if (!isEqual(prevProps.manuals, manuals)) {
      sorted = manuals;
      this.handleSortChange();
    }

    if (prevProps.isMobile !== isMobile) {
      let states = { activePage, perPage: limit };
      // if smaller than 1024 and limit is not 10, force limit to 10
      if (isMobile && limit !== PAGE_SIZE_OPTIONS_MOBILE) {
        states = { ...states, perPage: PAGE_SIZE_OPTIONS_MOBILE };
      }
      // if larger than 1024 and limit is 10, force limit to PAGE_SIZE_OPTIONS[0]
      if (!isMobile && limit === PAGE_SIZE_OPTIONS_MOBILE) {
        states = { ...states, perPage: PAGE_SIZE_OPTIONS[0] };
      }

      // if states is updated, do these:
      if (!isEqual(states, { activePage, perPage: limit })) {
        this.setState({ activePage, limit: states.perPage });
        this.handlePaginationChange(null, states);
      }
    }
  }

  setPageSize = e => {
    if (!e) {
      return;
    }

    this.handlePaginationChange(e, {
      activePage: this.state.activePage,
      perPage: Number(e.target.value)
    });
  };

  // only for mobile
  handleLoadMoreClick = () => {
    const { activePage, limit } = this.state;
    const newActivePage = activePage + 1;
    const activeItems = sorted && sorted.slice(0, newActivePage * limit);
    this.setState({ activePage: newActivePage, activeItems });
  };

  handlePaginationChange = (e, { activePage, perPage }) => {
    const limit = perPage || this.state.limit;
    for (let i = activePage; i > 0; i--) {
      const start = limit === PAGE_SIZE_OPTIONS_MOBILE ? 0 : (i - 1) * limit;
      const activeItems = sorted && sorted.slice(start, i * limit);

      if (activeItems && activeItems.length) {
        this.setState({ activePage: i, activeItems, limit });
        break;
      }
    }
  };

  handleSortChange = (key = sortNames[0], isDate = false) => {
    if (!sorted) {
      return;
    }

    const desc = this.state.isDesc[key] || false;
    const activePage = 1;
    const operator = desc ? -1 : 1;
    sorted = isDate
      ? sorted.sort(
          (a, b) => new Date(b[key]) * operator - new Date(a[key]) * operator
        )
      : sorted.sort((a, b) => (b[key] > a[key] ? operator : operator * -1));

    this.setState({
      activePage,
      isDesc: {
        ...this.state.isDesc,
        [key]: !desc
      },
      activeSort: key
    });
    this.handlePaginationChange(null, { activePage });
  };

  renderInit = () => (
    <SearchResultsPlaceholder
      images={[manualDefault]}
      header={
        <FormattedMessage
          id="subtitle.manuals_empty"
          description="Find a manual title"
          defaultMessage="Find a manual"
        />
      }
    >
      <p>
        <FormattedMessage
          id="description.manuals_empty"
          description="Find a manual description"
          defaultMessage="Complete the form above to find the manual you are looking for."
        />
      </p>
    </SearchResultsPlaceholder>
  );

  renderWarning = () => (
    <SearchResultsPlaceholder
      images={[manualNoResults]}
      header={
        <FormattedMessage
          id="subtitle.manuals_noresult"
          description="Manuals with no results title"
          defaultMessage="No results"
        />
      }
    >
      <p>
        <FormattedMessage
          id="description.assets_empty"
          defaultMessage="There doesn't seem to be any {assetType}s associated to the criteria you selected."
          values={{ assetType: 'manual' }}
        />
      </p>
      <p>
        <FormattedMessage
          id="description.no_result"
          defaultMessage="Try again with new criteria or {feedback} to let us know if you can't find the {assetType} you are looking for."
          values={{
            assetType: 'manual',
            feedback: (
              <a onClick={() => window.zE && window.zE.activate()}>
                use our feedback feature
              </a>
            )
          }}
        />
      </p>
    </SearchResultsPlaceholder>
  );

  renderResults = () => {
    const { activePage, activeItems, activeSort, isDesc, limit } = this.state;
    const { manuals, querySelections, isMobile } = this.props;
    const totalPages = manuals ? Math.ceil(manuals.length / limit) : 0;
    const enablePagination =
      (isMobile && manuals.length > PAGE_SIZE_OPTIONS_MOBILE) ||
      (!isMobile && manuals.length > PAGE_SIZE_OPTIONS[0]);

    const renderSortCell = (num, label, isDate = false) => (
      <Table.HeaderCell>
        <a
          onClick={() => this.handleSortChange(sortNames[num], isDate)}
          className="text--nowrap"
        >
          <SortIcon
            active={activeSort === sortNames[num]}
            isDesc={isDesc && isDesc[sortNames[num]]}
          >
            {label}
          </SortIcon>
        </a>
      </Table.HeaderCell>
    );

    return (
      <Table striped selectable className="container__inner">
        {!isMobile && (
          <Table.Header data-qa="manuals-header">
            <Table.Row data-qa="manuals-header">
              <Table.HeaderCell>Brand</Table.HeaderCell>
              {renderSortCell(
                1,
                <FormattedMessage
                  id="form.product_name"
                  defaultMessage="Product name"
                />
              )}
              {renderSortCell(
                2,
                <FormattedMessage
                  id="form.file_name"
                  defaultMessage="File name"
                />
              )}
              {renderSortCell(
                3,
                <FormattedMessage
                  id="form.language"
                  defaultMessage="Language"
                />
              )}
              {renderSortCell(
                4,
                <FormattedMessage
                  id="form.last_modified"
                  defaultMessage="Last modified"
                />,
                true
              )}
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>
        )}

        <Table.Body data-qa="search-results" data-testid="manuals-table-list">
          {activeItems &&
            activeItems.map(manual => (
              <Table.Row key={manual.ManualId}>
                <Table.Cell data-qa="product-name-search-result">
                  {querySelections.Brand || manual.ProductBrands}
                </Table.Cell>
                <Table.Cell>{manual.ProductName}</Table.Cell>
                <Table.Cell>
                  <a
                    href={`${manual.FileLink}`}
                    title={manual.ProductName}
                    target="stream"
                  >
                    <strong>{manual.FileName}</strong>
                  </a>
                </Table.Cell>
                <Table.Cell>
                  {querySelections.Language || manual.Language}
                </Table.Cell>
                <Table.Cell>{Formatters.date(manual.LastModified)}</Table.Cell>
                <StyledCTACell>
                  <AssetDownloadsTable.Cells.Download
                    cell={{
                      value: swapImageEndpointForDownload(manual.FileLink)
                    }}
                    row={{
                      original: { ...manual }
                    }}
                  />
                </StyledCTACell>
              </Table.Row>
            ))}
        </Table.Body>

        {enablePagination && (
          <Table.Footer data-testid="manuals-table-footer">
            <Table.Row>
              <Table.Cell colSpan={sortNames.length + 1} textAlign="center">
                {!isMobile && (
                  <Grid className="grid--spreaded">
                    <Grid.Column />
                    <Pagination
                      activePage={activePage}
                      onPageChange={this.handlePaginationChange}
                      totalPages={totalPages}
                      firstItem={null}
                      lastItem={null}
                      prevItem={{
                        content: <Icon name="angle left" />,
                        icon: true,
                        disabled: activePage === 1
                      }}
                      nextItem={{
                        content: <Icon name="angle right" />,
                        icon: true,
                        disabled: activePage === totalPages
                      }}
                      secondary
                    />
                    <AssetDownloadsTable.PageSizeField
                      onChange={this.setPageSize}
                    />
                  </Grid>
                )}

                {activeItems &&
                  activeItems.length < manuals.length &&
                  isMobile && (
                    <Button onClick={this.handleLoadMoreClick} secondary>
                      Load More
                    </Button>
                  )}
              </Table.Cell>
            </Table.Row>
          </Table.Footer>
        )}
      </Table>
    );
  };

  // TODO: error state??
  renderBasedOnStatus() {
    const { manuals, isLoading } = this.props;

    if (isLoading) {
      return <Loader active />;
    } else if (!manuals) {
      return this.renderInit();
    } else {
      return (
        <Fragment>
          <TotalPill total={manuals.length} unit="Result" />
          {!manuals.length ? this.renderWarning() : this.renderResults()}
        </Fragment>
      );
    }
  }

  render() {
    return this.renderBasedOnStatus();
  }
}

export default ManualsTable;
